import * as React from 'react'
import Box from '@mui/material/Box'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import { BaseRequirement, Scope } from '@shared/types'
import makeStyles from '@mui/styles/makeStyles'
import { useQuery } from 'react-query'
import { apiJSON } from '@shared/utils/api/api.util'
import { Dispatch, ReactNode, SetStateAction, useMemo } from 'react'
import { useRequirementsData } from '@shared/hooks/use-requirements-data.hook'
import { BackdropProgress } from '@shared/components/backdrop-progress.component'
import { uniq } from 'lodash'
import CompaniesSelectToRequirementsMappingComponent from '@modules/select-scopes/companies-select-to-requirements-mapping.component'

interface Props {
  selected: number[]
  setSelected: Dispatch<SetStateAction<number[]>>
  header: ReactNode
  type: 'requirements' | 'localization'
  scopes: Scope[]
  currentScope: Scope | null
}

enum TreeItemType {
  SCOPE = 'scope',
  GROUP = 'group',
  REQUIREMENT = 'requirement',
}

export default function RequirementsTreeAdmin({
  scopes,
  selected,
  setSelected,
  header,
  type,
  currentScope,
}: Props) {
  const classes = useStyles()
  const { data: baseRequirements, isLoading: areRequirementsFetching } =
    useQuery<BaseRequirement[]>('requirementbase/crud', () =>
      apiJSON('requirementbase/crud')
    )

  const findCurrentScope = scopes.find(
    (scope) => scope.scopeID == currentScope?.scopeID
  )

  const requirements = useMemo(
    () =>
      (baseRequirements ?? [])
        .map((requirement) => ({
          ...requirement,
          groupID: requirement.group.groupID,
        }))
        .filter((requirement) => requirement.isPublished),
    [baseRequirements]
  )

  const { isRequirementsDataFetching, groupsPerScopeValues } =
    useRequirementsData<BaseRequirement>(requirements, true)

  const groups = groupsPerScopeValues.find(
    (groups) => groups[0].scope.scopeID === currentScope?.scopeID
  )

  const handleItemSelectionToggle = (
    event: React.SyntheticEvent,
    itemId: string,
    isSelected: boolean
  ) => {
    if (itemId.includes(TreeItemType.SCOPE)) {
      const scopeId = +itemId.split('-')[1]
      setSelected((prevItems) => {
        let result = prevItems
        const groupsInScope = groupsPerScopeValues.find(
          (groups) => groups[0].scope.scopeID === scopeId
        )
        const requirementsIds =
          groupsInScope
            ?.map((group) =>
              group.requirements.map(
                (requirement) => requirement.requirementBaseID
              )
            )
            .flat() || []

        if (isSelected) {
          result = [...result, ...requirementsIds]
        } else {
          result = result.filter((item) => !requirementsIds.includes(item))
        }

        return uniq(result)
      })
      return
    }

    if (itemId.includes(TreeItemType.GROUP)) {
      const groupId = +itemId.split('-')[1]
      const foundGroup = groupsPerScopeValues
        .flat()
        .find((groups) => groups.groupID === groupId)

      if (foundGroup) {
        const requirementsIds = foundGroup.requirements.map(
          (requirement) => requirement.requirementBaseID
        )

        setSelected((prevItems) => {
          return uniq(
            isSelected
              ? [...prevItems, ...requirementsIds]
              : prevItems.filter((id) => !requirementsIds.includes(id))
          )
        })
      }

      return
    }

    if (itemId.includes(TreeItemType.REQUIREMENT)) {
      const requirementId = +itemId.split('-')[1]
      setSelected((prevItems) =>
        uniq(
          isSelected
            ? [...prevItems, requirementId]
            : prevItems.filter((id) => id !== requirementId)
        )
      )
      return
    }
  }

  const isDataLoading = isRequirementsDataFetching || areRequirementsFetching

  const selectedItems = useMemo(() => {
    if (isDataLoading) {
      return []
    }

    const result = selected.map(
      (itemID) => `${TreeItemType.REQUIREMENT}-${itemID}`
    )
    scopes.forEach((scope) => {
      const groups =
        groupsPerScopeValues.find(
          (groups) => groups[0].scope.scopeID === scope.scopeID
        ) || []
      const requirementsInScope: number[] = []
      groups.forEach((group) => {
        const requirementsIDs = group.requirements.map(
          (requirement) => requirement.requirementBaseID
        )
        requirementsInScope.push(...requirementsIDs)
        if (requirementsIDs.every((id) => selected.includes(id))) {
          result.push(`${TreeItemType.GROUP}-${group.groupID}`)
        }
      })
      if (requirementsInScope.every((id) => selected.includes(id))) {
        result.push(`${TreeItemType.SCOPE}-${scope.scopeID}`)
      }
    })

    return result
  }, [selected, scopes, groupsPerScopeValues, isDataLoading])

  if (isDataLoading) {
    return <BackdropProgress />
  }

  return (
    <Box className={classes.wrapper}>
      {type === 'localization' ? (
        <CompaniesSelectToRequirementsMappingComponent
          scopes={scopes}
          groupsPerScopeValues={groupsPerScopeValues}
          setSelected={setSelected}
        />
      ) : null}
      {header ? header : null}
      <SimpleTreeView
        multiSelect
        checkboxSelection
        onItemSelectionToggle={handleItemSelectionToggle}
        selectedItems={selectedItems}
      >
        {groups?.map((group) => (
          <TreeItem
            itemId={`${TreeItemType.GROUP}-${group.groupID}`}
            key={`${TreeItemType.GROUP}-${group.groupID}`}
            label={group.name}
          >
            {group.requirements.map((requirement) => (
              <TreeItem
                itemId={`${TreeItemType.REQUIREMENT}-${requirement.requirementBaseID}`}
                key={`${TreeItemType.REQUIREMENT}-${requirement.requirementBaseID}`}
                label={requirement.name}
              />
            ))}
          </TreeItem>
        ))}
      </SimpleTreeView>
    </Box>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    wrapper: {
      marginTop: theme.spacing(1),
    },
  }
})
